import React from 'react';
import { useSettingsStore } from '../../stores/settingsStore';

const BackgroundSelector: React.FC = () => {
  const { backgroundStyle, backgroundColor, backgroundImage, updateSettings } = useSettingsStore();

  const gradients = [
    { name: 'Warm Sunset', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
    { name: 'Cool Ocean', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
    { name: 'Soft Lavender', value: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)' },
    { name: 'Morning Mist', value: 'linear-gradient(135deg, #e0c3fc 0%, #9bb5ff 100%)' },
    { name: 'Peach Cream', value: 'linear-gradient(135deg, #ffecd2 0%, #fce38a 100%)' },
    { name: 'Sky Blue', value: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)' },
  ];

  const colors = [
    '#ffffff', '#f8f9fa', '#e9ecef', '#dee2e6',
    '#1a1a1a', '#2d3748', '#4a5568', '#718096'
  ];

  if (backgroundStyle === 'solid') {
    return (
      <div className="grid grid-cols-4 gap-3">
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => updateSettings({ backgroundColor: color })}
            className={`w-12 h-12 rounded-lg border-2 ${
              backgroundColor === color ? 'border-blue-500' : 'border-gray-300'
            }`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    );
  }

  if (backgroundStyle === 'gradient') {
    return (
      <div className="grid grid-cols-2 gap-3">
        {gradients.map((gradient) => (
          <button
            key={gradient.name}
            onClick={() => updateSettings({ backgroundColor: gradient.value })}
            className={`h-16 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
              backgroundColor === gradient.value ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
            }`}
            style={{ background: gradient.value }}
          >
            <span className="text-gray-700 font-medium drop-shadow-sm">{gradient.name}</span>
          </button>
        ))}
      </div>
    );
  }

  if (backgroundStyle === 'image') {
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // 이미지 파일만 허용
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드할 수 있습니다.');
        return;
      }

      // FileReader로 이미지를 base64로 변환
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;

        // settingsStore에 저장 (Zustand persist로 자동 localStorage 저장)
        updateSettings({
          backgroundImage: {
            id: 'custom',
            url: dataUrl,
            name: file.name,
            overlay: 0.3,
            blur: 0
          }
        });
      };

      reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
      updateSettings({ backgroundImage: null });
    };

    return (
      <div className="space-y-4">
        {/* 파일 업로드 버튼 */}
        <div>
          <input
            type="file"
            id="bg-image-upload"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <label
            htmlFor="bg-image-upload"
            className="w-full p-4 rounded-lg border-2 border-dashed border-gray-400 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-500 transition-all cursor-pointer flex flex-col items-center justify-center space-y-2 bg-gray-50 dark:bg-gray-800/50"
          >
            <span className="text-3xl">📂</span>
            <span className="text-sm font-medium">Upload Custom Image</span>
            <span className="text-xs text-gray-500">Click to select an image file</span>
          </label>
        </div>

        {/* 현재 이미지 미리보기 */}
        {backgroundImage && (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden border-2 border-blue-500">
              <div
                className="h-32 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${backgroundImage.url})`,
                  filter: `blur(${backgroundImage.blur}px)`
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: `rgba(0, 0, 0, ${backgroundImage.overlay})` }}
                />
              </div>
              <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {backgroundImage.name}
              </div>
            </div>

            {/* 이미지 제거 버튼 */}
            <button
              onClick={handleRemoveImage}
              className="w-full p-2 rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-all text-sm font-medium"
            >
              Remove Image
            </button>

            {/* Overlay 슬라이더 */}
            <div className="pt-3 border-t">
              <label className="block text-sm font-medium mb-1">
                Overlay: {Math.round(backgroundImage.overlay * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={backgroundImage.overlay}
                onChange={(e) => updateSettings({
                  backgroundImage: {
                    ...backgroundImage,
                    overlay: parseFloat(e.target.value)
                  }
                })}
                className="w-full"
              />
            </div>

            {/* Blur 슬라이더 */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Blur: {backgroundImage.blur}px
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={backgroundImage.blur}
                onChange={(e) => updateSettings({
                  backgroundImage: {
                    ...backgroundImage,
                    blur: parseInt(e.target.value)
                  }
                })}
                className="w-full"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default BackgroundSelector;